import * as React from 'react';
import { Session } from 'next-auth';
import { AuthenticatedUser } from '@ghq-abi/auth-client-lib';
import { TolgeeProvider } from '@tolgee/react';

import { DEFAULT_LANGUAGE } from '~/shared/constants/i18n';
import { usePreferredLanguage } from '~/shared/hooks/usePreferredLanguage';
import { useTolgeeSSR } from '~/shared/hooks/useTolgeeSSR';

type UserSessionCustomData = {
  language: string;
};

/**
 * Campos adicionais que, na v1 da auth-client-lib, faziam parte do
 * `AuthenticatedUser`. Na v2 o tipo da lib é enxuto e os dados extras são
 * providos via `additionalSessionDataCallback` (ver `~/shared/auth/authConfig`).
 * Mantemos essas propriedades opcionais para preservar o consumo existente.
 */
type CustomSessionData = {
  roles?: {
    isGlobalAdmin?: boolean;
  };
  managedZones?: string[];
  persona?: string;
};

export type CommonUserData = {
  globalId: string;
  name: string;
  zone: string;
  band: string;
  employeeId: string;
  token: string;
  refreshToken: string;
  homeLocation: string;
} & UserSessionCustomData &
  CustomSessionData;

export type BoilerplateSession = {
  country: string;
  email: string;
  proxiedAs?: CommonUserData | null;
} & AuthenticatedUser &
  CommonUserData;

export type BoilerplateCustomSessionData = {
  sessionInfo: BoilerplateSession;
  updateCurrentLanguage?: (newLanguage: string) => void;
};

type AuthProviderProps = {
  children: React.ReactNode;
  sessionInfo: Session | undefined;
};

const SessionInfoContext = React.createContext<BoilerplateCustomSessionData>(
  {} as BoilerplateCustomSessionData
);

export function SessionInfoProvider(props: AuthProviderProps) {
  const { children } = props;

  // Busca a linguagem preferida da API
  const { data: preferredLanguageData } = usePreferredLanguage();

  const [providerValue, setProviderValue] =
    React.useState<BoilerplateCustomSessionData>(() => {
      const { sessionInfo } = props;
      if (!sessionInfo) {
        return {} as BoilerplateCustomSessionData;
      }

      // Inicializa com localStorage ou session (a API será aplicada depois)
      const initialLanguage =
        (typeof window !== 'undefined' &&
          localStorage.getItem('currentLanguage')) ||
        sessionInfo.user.language;

      const boilerplateCustomSession: BoilerplateCustomSessionData = {
        sessionInfo: {
          globalId: sessionInfo.user.globalId,
          employeeId: sessionInfo.user.employeeId,
          name: sessionInfo.user.name,
          zone: sessionInfo.user.zone,
          band: sessionInfo.user.band,
          bandGroup: sessionInfo.user.bandGroup,
          country: sessionInfo.user.country || '',
          email: sessionInfo.user.email,
          language: initialLanguage,
          refreshToken: sessionInfo.user.refreshToken,
          token: sessionInfo.user.token,
          proxiedAs: null,
          homeLocation: sessionInfo.user.homeLocation,
          roles: sessionInfo.user.roles
        }
      };

      if (sessionInfo.user.proxiedAs) {
        boilerplateCustomSession.sessionInfo.proxiedAs = {
          language: initialLanguage,
          band: sessionInfo.user.proxiedAs.band,
          bandGroup: sessionInfo.user.proxiedAs.bandGroup,
          country: sessionInfo.user.proxiedAs.country || '',
          email: sessionInfo.user.proxiedAs.email,
          employeeId: sessionInfo.user.proxiedAs.employeeId,
          globalId: sessionInfo.user.proxiedAs.globalId,
          name: sessionInfo.user.proxiedAs.name,
          token: sessionInfo.user.proxiedAs.token,
          refreshToken: sessionInfo.user.proxiedAs.refreshToken,
          zone: sessionInfo.user.proxiedAs.zone,
          homeLocation: sessionInfo.user.proxiedAs.homeLocation,
          roles: sessionInfo.user.proxiedAs.roles
        };
      }

      return boilerplateCustomSession;
    });

  // Determina o locale: API > localStorage > session > default
  // Memoizado para evitar recalcular a cada render
  const locale = React.useMemo(
    () =>
      preferredLanguageData?.language ||
      (typeof window !== 'undefined' &&
        localStorage.getItem('currentLanguage')) ||
      providerValue.sessionInfo?.proxiedAs?.language ||
      providerValue.sessionInfo?.language ||
      DEFAULT_LANGUAGE,
    [preferredLanguageData?.language, providerValue.sessionInfo]
  );

  const ssrTolgee = useTolgeeSSR(locale);

  // Tentar forçar carregamento das traduções no SSR
  React.useEffect(() => {
    const isServer = typeof window === 'undefined';
    if (isServer && ssrTolgee) {
      // Tentar fazer fetch manualmente se o método run existir
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tolgeeInstance = ssrTolgee as any;
      if (typeof tolgeeInstance.run === 'function') {
        void tolgeeInstance.run().catch((error: unknown) => {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.error('[Tolgee] Erro ao carregar traduções no SSR:', error);
          }
        });
      }
    }
  }, [ssrTolgee]);

  // Atualiza a linguagem quando a API retornar dados
  // IMPORTANTE: Não atualiza Tolgee aqui para evitar loop com o próximo effect
  React.useEffect(() => {
    if (preferredLanguageData?.language) {
      const newLanguage = preferredLanguageData.language;

      setProviderValue(prevState => {
        // Evita atualização desnecessária se o idioma já é o mesmo
        const currentLanguage =
          prevState.sessionInfo?.proxiedAs?.language ||
          prevState.sessionInfo?.language;

        if (currentLanguage === newLanguage) {
          return prevState;
        }

        const updatedSessionInfo = { ...prevState.sessionInfo };

        if (updatedSessionInfo.proxiedAs) {
          updatedSessionInfo.proxiedAs.language = newLanguage;
        } else {
          updatedSessionInfo.language = newLanguage;
        }

        return { ...prevState, sessionInfo: updatedSessionInfo };
      });
    }
  }, [preferredLanguageData?.language]);

  // Sincronizar o locale com o Tolgee quando mudar
  // Este effect agora é responsável por todas as mudanças no Tolgee
  React.useEffect(() => {
    if (typeof window !== 'undefined' && ssrTolgee) {
      const currentLang = ssrTolgee.getLanguage();
      if (currentLang !== locale) {
        void ssrTolgee.changeLanguage(locale);
      }
    }
  }, [locale, ssrTolgee]);

  const updateCurrentLanguage = React.useCallback(
    (newLanguage: string) => {
      setProviderValue(prevState => {
        // Evita atualização desnecessária se o idioma já é o mesmo
        const currentLanguage =
          prevState.sessionInfo?.proxiedAs?.language ||
          prevState.sessionInfo?.language;

        if (currentLanguage === newLanguage) {
          return prevState;
        }

        const updatedSessionInfo = { ...prevState.sessionInfo };

        if (updatedSessionInfo.proxiedAs) {
          updatedSessionInfo.proxiedAs.language = newLanguage;
        } else {
          updatedSessionInfo.language = newLanguage;
        }

        // Mantém localStorage como fallback temporário
        if (typeof window !== 'undefined') {
          localStorage.setItem('currentLanguage', newLanguage);
        }

        return { ...prevState, sessionInfo: updatedSessionInfo };
      });

      // Atualiza o Tolgee fora do setState para evitar problemas
      if (ssrTolgee) {
        void ssrTolgee.changeLanguage(newLanguage);
      }
    },
    [ssrTolgee]
  );

  const memoizedValue = React.useMemo(
    () => ({
      sessionInfo: providerValue.sessionInfo,
      updateCurrentLanguage
    }),
    [providerValue.sessionInfo, updateCurrentLanguage]
  );

  return (
    <SessionInfoContext.Provider value={memoizedValue}>
      <TolgeeProvider tolgee={ssrTolgee}>{children}</TolgeeProvider>
    </SessionInfoContext.Provider>
  );
}

export function useSessionInfo() {
  const context = React.useContext(SessionInfoContext);

  if (!context) {
    throw new Error(
      'useSessionInfo must be used within an SessionInfoProvider'
    );
  }

  return context;
}
